// Membership screen shared by brokers and valuers: current plan, the tiers, history, and the pay sheet.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, Button, Card, Chip, ConfirmDialog, Dialog, ErrorNotice, Notice, PageHeader, Skeleton, Table } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ApiError, api } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { ME_KEY, type Me } from '@/lib/auth';
import { dateOnly, money } from '@/lib/format';
import type { MembershipPage as Page, Payment, Tier } from '@/lib/types';

declare global {
  interface Window {
    Moyasar?: { init: (opts: Record<string, unknown>) => void };
  }
}

export function MembershipPage({ role, me }: { role: 'broker' | 'evaluator'; me: Me }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const base = `/api/${role}`;
  const page = useQuery({ queryKey: [role, 'membership'], queryFn: () => api.get<Page>(`${base}/membership`) });
  const payments = useQuery({ queryKey: ['me', 'payments'], queryFn: () => api.get<{ items: Payment[] }>('/api/me/payments') });
  const [paying, setPaying] = useState<Tier | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const profile = role === 'broker' ? me.broker : me.evaluator;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: [role] });
    void qc.invalidateQueries({ queryKey: ME_KEY });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
  const start = useMutation({
    mutationFn: (input: { tierId: string; providerPaymentId: string }) => api.post(`${base}/membership/start`, input),
    onSuccess: () => { setPaying(null); setDone(t('broker.membership.started')); refresh(); },
  });
  const cancel = useMutation({
    mutationFn: () => api.post<{ ok: boolean; endsAt: string | null }>(`${base}/membership/cancel`),
    onSuccess: (r) => { setCancelOpen(false); setDone(r.endsAt ? t('broker.membership.cancelledUntil', { date: dateOnly(r.endsAt, locale) }) : t('broker.membership.cancelled')); refresh(); },
    onError: (e) => { setCancelOpen(false); setDone(null); setError(e); },
  });
  if (page.isLoading) return <Skeleton lines={8} />;
  if (page.error || !page.data) return <ErrorNotice error={page.error} retry={() => void page.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const p = page.data;
  const current = p.slots.tier;
  const status = profile?.membershipStatus ?? p.slots.membershipStatus;
  const active = status === 'active';
  // "Cancel renewal" leaves the membership active until the paid period ends; only then does it stop.
  const autoRenew = profile?.autoRenew !== false;
  const tierName = (tier: Tier | { nameAr: string; nameEn: string }) => (locale === 'ar' ? tier.nameAr : tier.nameEn);
  const membershipPayments = (payments.data?.items ?? []).filter((x) => x.kind === 'membership');
  const tierById = (id: string) => p.tiers.find((x) => x.id === id);
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: `/${role}`, label: t('nav.dashboard') }, { label: t('nav.membership') }]} title={t('nav.membership')} meta={t(role === 'broker' ? 'broker.membership.intro' : 'broker.membership.introEv')} />
      {done ? <Notice tone="success">{done}</Notice> : null}
      {error ? <Notice tone="error">{errorText(error, t)}</Notice> : null}
      {status === 'past_due' ? <Notice tone="warning">{t('broker.membership.pastDueNotice')}</Notice> : null}
      <Card lift>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="label">{t('broker.membership.current')}</p>
            {active && current ? (
              <>
                <h2 className="mt-1">{t('broker.membership.currentTier', { tier: tierName(current), n: current.slots })}</h2>
                <p className="text-muted text-[14px] mt-1">
                  {autoRenew
                    ? t('broker.membership.renews', { date: dateOnly(profile?.membershipPeriodEnd, locale), price: money(p.tiers.find((x) => x.id === current.id)?.priceHalalas, locale) })
                    : t('broker.membership.renewalCancelledEnds', { date: dateOnly(profile?.membershipPeriodEnd, locale) })}
                </p>
              </>
            ) : (
              <>
                <h2 className="mt-1">{t('broker.membership.noMembership')}</h2>
                <p className="text-muted text-[14px] mt-1">{t('broker.membership.chooseHint')}</p>
              </>
            )}
          </div>
          {active && autoRenew ? <Button variant="text" className="text-error" onClick={() => setCancelOpen(true)}>{t('broker.membership.cancelRenewal')}</Button> : null}
        </div>
        {active ? (
          <>
            <div className="flex justify-between text-[13px] mt-4"><span className="text-muted">{t('broker.membership.usedNow')}</span><span className="num">{p.slots.used} / {p.slots.max}</span></div>
            <Bar value={p.slots.used} max={p.slots.max} className="mt-1" />
          </>
        ) : null}
      </Card>
      <div className="grid sm:grid-cols-3 gap-3">
        {p.tiers.filter((x) => x.active).map((tier) => {
          const isCurrent = active && current?.id === tier.id;
          return (
            <Card key={tier.id} className={isCurrent ? 'border-accent shadow-[inset_0_0_0_1px_var(--accent)]' : ''}>
              <p className={`label ${isCurrent ? 'text-accent' : ''}`}>{tierName(tier)}{isCurrent ? ` · ${t('broker.membership.currentPlan')}` : ''}</p>
              <p className="num text-[32px] font-light my-1.5">{Math.round(tier.priceHalalas / 100)} <span className="text-[15px]">{t('app.perMonth')}</span></p>
              <p className="text-fg-2 text-[14px]">{t('app.slotsN', { n: tier.slots })}</p>
              <Button variant={isCurrent ? 'secondary' : active ? 'primary' : 'primary'} className="w-full mt-3.5" disabled={isCurrent} onClick={() => setPaying(tier)}>
                {isCurrent ? t('broker.membership.currentPlan') : active ? t('broker.membership.upgradeNow') : t('broker.membership.subscribe')}
              </Button>
            </Card>
          );
        })}
      </div>
      <Card>
        <h2 className="mb-3">{t('broker.membership.history')}</h2>
        {membershipPayments.length ? (
          <Table>
            <thead><tr><th>{t('broker.membership.payment')}</th><th>{t('broker.membership.tier')}</th><th>{t('app.amount')}</th><th>{t('broker.membership.status')}</th><th>{t('app.date')}</th></tr></thead>
            <tbody>
              {membershipPayments.map((x) => (
                <tr key={x.id}>
                  <td className="num">{x.number}</td>
                  <td>{tierById(x.relatedId) ? tierName(tierById(x.relatedId)!) : '—'} · {x.description?.includes('تجديد') ? t('broker.membership.renewal') : t('broker.membership.subscription')}</td>
                  <td className="num">{money(x.amountHalalas, locale)}</td>
                  <td><Chip form={x.status === 'paid' ? 'filled' : x.status === 'pending' ? 'outlined' : x.status === 'failed' ? 'warning' : 'muted'}>{t(`status.${x.status}`)}</Chip></td>
                  <td className="whitespace-nowrap tabular-nums">{dateOnly(x.paidAt ?? x.createdAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : <p className="text-muted text-[14px]">{t('app.none')}</p>}
      </Card>
      <ConfirmDialog open={cancelOpen} onClose={() => setCancelOpen(false)} title={t('broker.membership.cancelTitle')} body={t('broker.membership.cancelBody')} confirmLabel={t('broker.membership.cancelRenewal')} cancelLabel={t('app.back')} danger loading={cancel.isPending} onConfirm={() => cancel.mutate()} />
      {paying ? <PaySheet tier={paying} publishableKey={p.publishableKey} onClose={() => setPaying(null)} onPaid={(id) => start.mutate({ tierId: paying.id, providerPaymentId: id })} error={start.error instanceof ApiError ? start.error.code : null} loading={start.isPending} /> : null}
    </div>
  );
}

/** Moyasar's hosted form for real keys; with the mock provider a one-tap simulated payment. */
function PaySheet({ tier, publishableKey, onClose, onPaid, error, loading }: { tier: Tier; publishableKey: string | undefined; onClose: () => void; onPaid: (providerPaymentId: string) => void; error: string | null; loading: boolean }) {
  const { t, locale } = useI18n();
  const mock = !publishableKey || publishableKey === 'pk_test_mock';
  const ref = useRef<HTMLDivElement>(null);
  const [simulating, setSimulating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  useEffect(() => {
    if (mock || !ref.current) return;
    const mount = () => {
      if (!window.Moyasar || !ref.current) return;
      ref.current.innerHTML = '';
      window.Moyasar.init({
        element: ref.current,
        amount: tier.priceHalalas,
        currency: 'SAR',
        description: `${locale === 'ar' ? tier.nameAr : tier.nameEn} — Wasity`,
        publishable_api_key: publishableKey,
        callback_url: window.location.href,
        methods: ['creditcard', 'applepay'],
        credit_card: { save_card: true },
        on_completed: (payment: { id: string }) => onPaid(payment.id),
        on_failure: (e: unknown) => setFormError(String(e)),
      });
    };
    if (window.Moyasar) mount();
    else {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.css';
      document.head.appendChild(css);
      const s = document.createElement('script');
      s.src = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.js';
      s.onload = mount;
      document.head.appendChild(s);
    }
  }, [mock, tier, publishableKey, locale, onPaid]);
  const simulate = async () => {
    setSimulating(true);
    try {
      const r = await api.post<{ id: string }>('/api/dev/payments/simulate', { amountHalalas: tier.priceHalalas, token: 'tok_saved' });
      onPaid(r.id);
    } finally {
      setSimulating(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title={t('broker.membership.payTitle', { tier: locale === 'ar' ? tier.nameAr : tier.nameEn })}>
      <p className="text-[15px] text-fg-2 mb-4">{t('broker.membership.payHint', { price: money(tier.priceHalalas, locale) })}</p>
      {mock ? <Notice tone="info" className="mb-4">{t('broker.membership.payMock')}</Notice> : <div ref={ref} className="mb-4 min-h-24" />}
      {error || formError ? <Notice tone="error" className="mb-4">{errorText(error ?? formError, t)}</Notice> : null}
      <div className="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose}>{t('app.back')}</Button>
        {mock ? <Button variant="primary" loading={simulating || loading} onClick={() => void simulate()}>{t('broker.membership.payButton', { price: money(tier.priceHalalas, locale) })}</Button> : null}
      </div>
    </Dialog>
  );
}
